import {
    canAccessAnalystStudio,
    canCreateCatalogPrivilege,
    canCreateOrEditConnections,
    canLiveboardVerifyPrivilege,
    canManageCustomCalendars,
    canManageWorksheetViewsTables,
    canUserManageData,
    enableThemeBuilder,
    getCurrentOrgId,
    getDeveloperNavConfig,
    getSessionInfo,
    hasAdminPrivileges,
    hasDataWorkspacePrivilege,
    hasDeveloperPrivileges,
    hasSchedulingPrivileges,
    hasSpotAppAdminPrivilege,
    hasSpotAppsPrivilege,
    hasSyncManagementPrivilege,
    isA3Enabled,
    isAnalystStudioEnabled,
    isCaptainEnabled,
    isConvAssistEnabled,
    isDataflowEnabled,
    isEmbraceEnabled,
    isFoldersEnabled,
    isMonitorV2Enabled,
    isOrgsHiddenForDevelopTab,
    isOrgsOnFTEnabled,
    isRestAPIV20PlaygroundEnabled,
    isRolesEnabled,
    isScheduleOnHomeEnabled,
    isSchedulingJobEnabled,
    isScriptabilityEnabled,
    isSemanticIntegrationEnabled,
    isWebhookLCMEnabled,
} from '@thoughtspot/session-service';
import { hasSchedulePageAccessPrivilege } from '../../routing/privilege-list';

type PathRule = {
    match: (path: string) => boolean;
    check: () => boolean;
};

const hasDeveloperOrAdmin = () =>
    hasDeveloperPrivileges() || hasAdminPrivileges();

const INSIGHTS_RULES: PathRule[] = [
    { match: p => p === '/home/spotiq-analysis', check: isA3Enabled },
    {
        match: p => p === '/insights/home/liveboard-schedules',
        check: () =>
            isScheduleOnHomeEnabled() && hasSchedulePageAccessPrivilege(),
    },
    {
        match: p => p === '/home/monitor-alerts' || p === '/monitor',
        check: isMonitorV2Enabled,
    },
    { match: p => p === '/schedules', check: hasSchedulePageAccessPrivilege },
    { match: p => p === '/conv-assist', check: isConvAssistEnabled },
    {
        match: p =>
            p === '/insights/collections' || p === '/insights/doc-search',
        check: isFoldersEnabled,
    },
];

const DEVELOP_RULES: PathRule[] = [
    {
        match: p =>
            p === '/develop/actionsCustomization' ||
            p === '/develop/securitySettings' ||
            p === '/develop/styleCustomization' ||
            p === '/develop/links',
        check: hasDeveloperOrAdmin,
    },
    {
        match: p => p === '/develop/api/rest/playgroundV1',
        check: () =>
            hasDeveloperOrAdmin() &&
            !getDeveloperNavConfig().hideRestV1Playground,
    },
    {
        match: p => p === '/develop/api/rest/playgroundV2_0',
        check: () => hasDeveloperOrAdmin() && isRestAPIV20PlaygroundEnabled(),
    },
    {
        match: p => p === '/develop/playground/theme-builder',
        check: () => hasDeveloperOrAdmin() && enableThemeBuilder(),
    },
    {
        match: p => p === '/develop/webhooks',
        check: () => hasDeveloperOrAdmin() && isWebhookLCMEnabled(),
    },
];

const DATA_RULES: PathRule[] = [
    {
        match: p => p === '/data/memory',
        check: () => hasAdminPrivileges() || isConvAssistEnabled(),
    },
    {
        match: p => p === '/data/destination-sync',
        check: () => hasAdminPrivileges() || hasSyncManagementPrivilege(),
    },
    {
        match: p => p === '/data/data-governance',
        check: () => hasAdminPrivileges() || canCreateCatalogPrivilege(),
    },
    {
        match: p => p === '/data/liveboard-verification',
        check: () => hasAdminPrivileges() || canLiveboardVerifyPrivilege(),
    },
    {
        match: p =>
            p === '/data/usage-statistics' || p === '/data/business-data-model',
        check: hasAdminPrivileges,
    },
    {
        match: p => p === '/data/spotapps-admin',
        check: hasSpotAppAdminPrivilege,
    },
    {
        match: p => p === '/data/spotapp',
        check: () =>
            isEmbraceEnabled() && canUserManageData() && hasSpotAppsPrivilege(),
    },
    { match: p => p === '/data/dataflow', check: isDataflowEnabled },
    { match: p => p === '/data/explore', check: isA3Enabled },
    {
        match: p => p === '/data/dataset',
        check: () => isAnalystStudioEnabled() && canAccessAnalystStudio(),
    },
    { match: p => p === '/data/app-connections', check: isCaptainEnabled },
    {
        match: p => p === '/data/embrace' || p === '/data/embrace/connection',
        check: () =>
            canCreateOrEditConnections() || canManageWorksheetViewsTables(),
    },
    {
        match: p =>
            p === '/data/views' ||
            p === '/data/sql-view' ||
            p === '/data/rls' ||
            p === '/data/dbt',
        check: canManageWorksheetViewsTables,
    },
    {
        match: p => p === '/data/semantic-integrations',
        check: () => {
            if (!isSemanticIntegrationEnabled()) return false;
            return isRolesEnabled()
                ? canManageWorksheetViewsTables()
                : isEmbraceEnabled() && canUserManageData();
        },
    },
    {
        match: p => p === '/data/custom-calendar',
        check: canManageCustomCalendars,
    },
    {
        match: p => p === '/data/liveboard-schedules',
        check: () =>
            (hasAdminPrivileges() ||
                (canManageWorksheetViewsTables() &&
                    hasSchedulingPrivileges())) &&
            isSchedulingJobEnabled(),
    },
];

const resolve = (rules: PathRule[], path: string): boolean | null => {
    const rule = rules.find(r => r.match(path));
    return rule ? rule.check() : null;
};

/**
 * Determines whether a spotlight navigation path is accessible for the
 * current user, based on their privileges and enabled feature flags.
 *
 * Returns false if session is not yet loaded, or if any privilege check
 * throws — so errors here never bubble up to crash the app.
 *
 * @param path - The route path of the spotlight item (e.g. '/data/memory')
 * @param registeredPaths - Set of all paths registered in routePaths (fallback)
 */
export function getSpotlightPathPrivilege(
    path: string,
    registeredPaths: Set<string>,
): boolean {
    try {
        if (!getSessionInfo()) return false;

        if (path.startsWith('/insights')) {
            return resolve(INSIGHTS_RULES, path) ?? true;
        }

        if (path === '/teams/data-usage') return isOrgsOnFTEnabled();

        if (path.startsWith('/develop')) {
            if (isOrgsHiddenForDevelopTab()) {
                const orgId = getCurrentOrgId();
                const isPrimaryOrg = [0, -1].includes(orgId);
                if (!isPrimaryOrg) return false;
            }
            return resolve(DEVELOP_RULES, path) ?? hasDeveloperOrAdmin();
        }

        if (path === '/data/spotapp-export') {
            return isScriptabilityEnabled();
        }

        if (path.startsWith('/schema-viewer')) {
            return canManageWorksheetViewsTables();
        }

        if (path.startsWith('/data')) {
            return resolve(DATA_RULES, path) ?? hasDataWorkspacePrivilege();
        }

        if (path.startsWith('/import-tsl')) {
            return isScriptabilityEnabled();
        }

        return registeredPaths.has(path);
    } catch {
        return false;
    }
}
